import React from 'react';
import { Box, Card, CardContent, Chip, Paper, Radio, TextField, Typography } from '@mui/material';
import { AttachMoney } from '@mui/icons-material';

interface InstitucionalNucleusSelectorProps {
  nucleusCapacityPacks: Array<{ capacity: number; price: number }>;
  nucleusCount: number;
  onNucleusCountChange: (count: number) => void;
  calculateNucleusCapacityCost: (count: number) => number;
  formatCurrency: (value: number) => string;
}

/**
 * Seletor de quantidade de núcleos do plano Institucional (pacotes pré-definidos + quantidade
 * personalizada). Extraído de UnifiedPricingCalculator.tsx (F7).
 */
export const InstitucionalNucleusSelector: React.FC<InstitucionalNucleusSelectorProps> = ({
  nucleusCapacityPacks,
  nucleusCount,
  onNucleusCountChange,
  calculateNucleusCapacityCost,
  formatCurrency,
}) => {
  const isCustom = nucleusCount > 0 && !nucleusCapacityPacks.some((p) => p.capacity === nucleusCount);

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AttachMoney sx={{ mr: 1, color: 'primary.main' }} />
          Quantidade de núcleos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Selecione quantos núcleos você pretende gerenciar:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[...nucleusCapacityPacks]
            .sort((a, b) => a.capacity - b.capacity)
            .map((pack) => {
              const isSelected = nucleusCount === pack.capacity;
              return (
                <Paper
                  key={pack.capacity}
                  onClick={() => onNucleusCountChange(pack.capacity)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: 2,
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected ? 'primary.main' : 'background.paper',
                    color: isSelected ? 'white' : 'inherit',
                    '&:hover': {
                      bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                      borderColor: 'primary.main',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Radio checked={isSelected} sx={isSelected ? { color: 'white', '&.Mui-checked': { color: 'white' } } : {}} />
                      <Typography sx={{ color: 'inherit' }}>Até {pack.capacity} núcleos</Typography>
                    </Box>
                    <Chip label={formatCurrency(pack.price) + '/mês'} size="small" variant="outlined" sx={isSelected ? { color: 'white', borderColor: 'rgba(255,255,255,0.5)' } : {}} color="primary" />
                  </Box>
                </Paper>
              );
            })}
          <Paper
            onClick={() => document.getElementById('calculator-custom-nucleus-input')?.focus()}
            sx={{
              p: 2,
              cursor: 'pointer',
              border: 2,
              borderColor: isCustom ? 'primary.main' : 'divider',
              bgcolor: isCustom ? 'primary.main' : 'background.paper',
              color: isCustom ? 'white' : 'inherit',
              '&:hover': { borderColor: 'primary.main' },
              transition: 'all 0.2s ease',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Radio checked={isCustom} sx={isCustom ? { color: 'white', '&.Mui-checked': { color: 'white' } } : {}} />
              <Typography sx={{ color: 'inherit' }}>Outra quantidade:</Typography>
              <TextField
                id="calculator-custom-nucleus-input"
                type="number"
                size="small"
                value={nucleusCount || ''}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  onNucleusCountChange(isNaN(v) || v < 0 ? 0 : v);
                }}
                inputProps={{ min: 0, style: { width: '80px', textAlign: 'center' } }}
                sx={{ width: '100px' }}
              />
              <Typography variant="body2" sx={{ color: isCustom ? 'rgba(255,255,255,0.9)' : 'text.secondary' }}>núcleos</Typography>
              {nucleusCount > 0 && (
                <Chip
                  label={formatCurrency(calculateNucleusCapacityCost(nucleusCount)) + '/mês'}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
            </Box>
          </Paper>
        </Box>
      </CardContent>
    </Card>
  );
};

export default InstitucionalNucleusSelector;
